// -----------------------------------------------------------------------------
// useV2ChecklistAutocomplete
//
// Fetches a lead's open V2 checklist items and exposes a queue-and-commit API
// for the FollowUpV2 modal's auto-complete mechanic.
//
// Returns:
//   items             — flat array of checklist items (all states, sorted due_at ASC)
//   openItemsByType   — { WHATSAPP_SENT: [...], LOG_PHONE_CALL: [...], LEAD_NEXT_ACTION: [...] }
//                       open items only, sorted due_at ASC per type
//   queuedIds         — Set of item IDs currently queued for completion
//   queuedCount       — queuedIds.size
//   queueComplete(itemType) — queues oldest open item of that type (no-op if none)
//   commitQueued()    — POSTs complete/ for each queued item; atomic, fires on Finish
//   discardQueued()   — resets queue (called on modal X / Escape — no POST)
//   loading           — true while initial fetch is in flight
//   refresh()         — force re-fetch (e.g. after external state change)
//
// Auto-complete rule: strict item-type match, oldest-open-wins by due_at ASC.
// Queue stores item IDs (not types) to prevent double-queuing the same item.
//
// Data sources (parallel):
//   GET /api/v1/practices/{p}/leads/{l}/task-checklists/  — template-applied checklist items
//   GET /api/v1/practices/{p}/tasks/?lead={l}&status__in=pending,in_progress  — standalone tasks (template_id=null)
//   Feature-gated: both return empty when tasks-v2 is not enabled for the practice.
//
// Complete endpoint: POST /api/v1/practices/{p}/tasks/{taskId}/complete/
//   Same endpoint used by completeTask in taskSlice.
// -----------------------------------------------------------------------------
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect, useRef } from 'react';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { buildTemplateStatusMap, filterGroupsByCurrentStatus, isChecklistItemDone, resolveCompletionState } from 'src/utils/checklistViewModel';
import { CHECKLIST_ITEM_TYPE } from 'src/constants/tasksV2';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { parseApiDate } from 'src/utils/taskDateUtils';

// Item types that FollowUpV2 can auto-complete (the action-driven reminder types
// that get matched + auto-completed when the user logs a call / sends a WhatsApp).
// This stays narrow — it drives buildOpenItemsByType's auto-match buckets only.
const AUTO_COMPLETE_TYPES = [
    CHECKLIST_ITEM_TYPE.WHATSAPP_SENT,
    CHECKLIST_ITEM_TYPE.LOG_PHONE_CALL,
    CHECKLIST_ITEM_TYPE.LEAD_NEXT_ACTION,
];

// Item types that are DISPLAYED in the checklist. Broader than AUTO_COMPLETE_TYPES
// so the attended pop-up shows the whole checklist — including EMAIL_SENT (e.g.
// "Same Day Email & Smile Simulation"), CONFIRMATION (e.g. "Same Day ClinCheck
// Call", "Open Follow Up") and SEND_CONSENT_FORM. Each renders + actions via the
// shared ActionPanel (email->composer, confirmation->done/skip). Auto-completion
// matching is unchanged (still keyed on AUTO_COMPLETE_TYPES).
const DISPLAYABLE_TYPES = [
    ...AUTO_COMPLETE_TYPES,
    CHECKLIST_ITEM_TYPE.EMAIL_SENT,
    CHECKLIST_ITEM_TYPE.CONFIRMATION,
    CHECKLIST_ITEM_TYPE.SEND_CONSENT_FORM,
    CHECKLIST_ITEM_TYPE.PHONE_CALLED,
    CHECKLIST_ITEM_TYPE.LEAD_STATUS_CHANGED,
];

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Flatten task-checklists response into a single item array.
 * Each item inherits its parent task's id (as taskId) so we can POST complete/.
 *
 * The complete endpoint requires the task ID, not the checklist-item ID.
 */
function buildFlatItems(groups) {
    const items = [];

    (groups || []).forEach((group) => {
        (group.tasks || []).forEach((task) => {
            // When the parent task is completed (e.g. the cascade fired by marking the
            // task done from the lead-detail view) its child checklist items can keep a
            // STALE completion_state ('NO'/null, completed_at null) — the BE cascade
            // updates the task row, not every item. Treat every item of a completed task
            // as done so a finished task never surfaces as an OPEN reminder item. Mirrors
            // the same guard in useGuidedChecklist.buildFlatItems.
            const taskCompleted = ['completed', 'archived'].includes(String(task.status || '').toLowerCase());
            (task.checklist_items || []).forEach((item) => {
                const resolvedType = item.item_type || item.action_type || '';
                // Only surface displayable checklist types (broader than the
                // auto-complete set so EMAIL_SENT / CONFIRMATION / consent items show).
                if (!DISPLAYABLE_TYPES.includes(resolvedType)) return;

                const done = isChecklistItemDone(item) || taskCompleted;
                const dueAt = task.due_at || null;

                // 2026-06-07 hotfix #79: use resolveCompletionState() to normalize the
                // BE's completion_state into a canonical set ('OPEN' | 'YES' | 'SKIPPED'
                // | 'PAUSED'). The prior commit (82e5daf1) preserved the BE value
                // verbatim, but BE returns 'NO' for open items — downstream code checking
                // `=== 'OPEN'` bucketed 'NO' as done → checklist hero disappeared and
                // every open item rendered as completed. The normalizer keeps PAUSED +
                // SKIPPED + paused_until intact (so Resume / Pause cards still work)
                // while mapping 'NO' / null / undefined → 'OPEN'.
                items.push({
                    id: String(item.id ?? item.checklist_item_id ?? `${task.id}-item`),
                    taskId: task.id,           // used for POST /tasks/{taskId}/checklist/{itemId}/complete|reopen|pause/
                    itemId: item.id ?? item.checklist_item_id,  // checklist item ID for /checklist/<itemId>/ endpoints
                    title: item.title || item.name || resolvedType,
                    item_type: resolvedType,
                    action_config: item.action_config,
                    completion_state: taskCompleted ? 'YES' : resolveCompletionState(item),
                    paused_until: item.paused_until || null,
                    due_at: dueAt,
                    due_label: task.due_human || (dueAt ? new Date(dueAt).toLocaleDateString() : ''),
                    overdue: !done && dueAt ? new Date(dueAt) < new Date() : false,
                    // 2026-06-07 #87 — template_checklist_item UUID is required by
                    // FollowUpPanel.jsx:333-341 + ActionPanel.jsx:185-190 to call
                    // /practices/<p>/checklist-email-mappings/resolve/ and pre-load
                    // the practice's mapped engagement template into the email
                    // composer. Without it the resolve guard silently bails →
                    // composer opens blank even when the BE mapping exists.
                    template_item: item.template_item || item.template_checklist_item_id || null,
                    leadTreatment: task.lead_treatment?.id ?? task.lead_treatment ?? null,
                    // Bug fix (FollowUpV2 "0 assigned" — lead 16875): without rawTask,
                    // TaskAssigneeHeads' `item.rawTask?.assignees || []` (ScheduleReminderTaskSection.jsx,
                    // UpNextCardV2.jsx) always fell back to [] regardless of server
                    // state. `task` here is a /task-checklists/ row, which already
                    // carries `assignees: [{id: <user_id>, name}]` in the right shape
                    // (apps/tasks/lead_checklist_views.py `_task_dict`) — mirrors
                    // useGuidedChecklist.buildFlatItems, which does this correctly.
                    rawTask: task,
                });
            });
        });
    });

    // Sort open items due_at ASC; done items to the end
    return items.sort((a, b) => {
        if (a.completion_state !== b.completion_state) {
            return a.completion_state === 'OPEN' ? -1 : 1;
        }
        if (!a.due_at && !b.due_at) return 0;
        if (!a.due_at) return 1;
        if (!b.due_at) return -1;
        return new Date(parseApiDate(a.due_at)) - new Date(parseApiDate(b.due_at));
    });
}

/**
 * Group open items by type, sorted due_at ASC.
 * Used to answer "which item gets auto-completed when the user picks X action".
 */
function buildOpenItemsByType(flatItems) {
    const byType = {};
    AUTO_COMPLETE_TYPES.forEach((t) => { byType[t] = []; });

    flatItems.forEach((item) => {
        if (item.completion_state !== 'OPEN') return;
        if (byType[item.item_type]) {
            byType[item.item_type].push(item);
        }
    });

    return byType;
}

// Map task.method → ACTION_META key so standalone tasks show the same
// method-specific icon as template checklist items (phone/WhatsApp/email).
// Falls back to 'STANDALONE_TASK' (→ DEFAULT_ACTION_META generic checkbox)
// when method is absent or unrecognised.
const TASK_METHOD_TO_ITEM_TYPE = {
    call:      'LOG_PHONE_CALL',
    whatsapp:  'WHATSAPP_SENT',
    email:     'EMAIL_SENT',
};

/**
 * Map open standalone tasks (template_id == null) to the flat-item shape the
 * popup consumes.
 *
 * item_type is derived from task.method so the popup shows the same call/
 * WhatsApp/email icon as template checklist items, not a generic checkbox.
 * Falls back to STANDALONE_TASK (→ DEFAULT_ACTION_META) when method is absent.
 *
 * Key differences from checklist items built by buildFlatItems:
 *  - itemId is null — no checklist-item row exists for this task.
 *  - isStandaloneTask: true — signals FollowUpV2's handleCardMarkComplete to
 *    POST to /tasks/{taskId}/complete/ (task-level) instead of the checklist
 *    item endpoint, which would 404 without an itemId.
 *  - rawTask preserved — TaskAssigneeHeads reads rawTask.assignees for display.
 *
 * Bug fix (FollowUpV2 "0 assigned" — lead 16875): rows from
 * GET /tasks/?lead={leadId} (TaskReadSerializer) expose `assignments`
 * (TaskAssignmentReadSerializer: [{ practice_user: { user: { id }, full_name }, ... }]),
 * NOT the `assignees: [{id, name}]` shape TaskAssigneeHeads reads via
 * `item.rawTask?.assignees || []`. Passing `task` through verbatim as rawTask
 * therefore always rendered 0 assigned regardless of server state, even
 * though rawTask itself was "preserved". normaliseAssignees() below bridges
 * the two shapes so rawTask.assignees always exists in the shape the
 * assignee-heads UI expects (matching `pu.user?.id` in
 * UpNextCardV2.jsx's hydratedInitial).
 */
function normaliseAssignees(task) {
    if (Array.isArray(task.assignees)) return task.assignees;
    return (task.assignments || []).map((a) => ({
        id: a.practice_user?.user?.id,
        name: a.practice_user?.full_name,
    }));
}

export function buildStandaloneItems(tasks) {
    return (tasks || []).map((task) => {
        const dueAt = task.due_at || null;
        const done = task.status === 'completed' || task.status === 'archived';
        // Map method → ACTION_META key for icon/colour consistency with checklist items
        const itemType = TASK_METHOD_TO_ITEM_TYPE[task.method] || 'STANDALONE_TASK';
        return {
            id: `standalone-${task.id}`,   // prefix avoids collisions with checklist-item IDs
            taskId: task.id,
            itemId: null,                  // no checklist item — handleCardMarkComplete branches on this
            title: task.title || 'Task',
            description: task.description || null, // shown as notes below title in UpNextCardV2
            item_type: itemType,           // method-derived for icon; falls back to STANDALONE_TASK
            completion_state: done ? 'YES' : 'OPEN',
            paused_until: null,
            due_at: dueAt,
            due_label: dueAt ? new Date(dueAt).toLocaleDateString() : '',
            overdue: !done && dueAt ? new Date(dueAt) < new Date() : false,
            template_item: null,
            isStandaloneTask: true,
            // for TaskAssigneeHeads assignee display — assignees normalised so
            // rawTask.assignees always exists in the {id: userId, name} shape.
            rawTask: { ...task, assignees: normaliseAssignees(task) },
            leadTreatment: task.lead_treatment?.id ?? task.lead_treatment ?? null,
        };
    });
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export default function useV2ChecklistAutocomplete({ practiceId, leadId }) {
    const [loading, setLoading] = useState(true);
    const [rawItems, setRawItems] = useState([]);   // items from API, no queue overlay
    const [rawGroups, setRawGroups] = useState([]); // unfiltered API response for debug overlay
    const [queuedIds, setQueuedIds] = useState(new Set());

    // Prevents duplicate Sentry warnings per lead
    const warnedLeadRef = useRef(null);

    // items with QUEUED state overlaid on top of rawItems
    const items = rawItems.map((item) =>
        queuedIds.has(item.id) && item.completion_state === 'OPEN'
            ? { ...item, completion_state: 'QUEUED' }
            : item
    );

    const openItemsByType = buildOpenItemsByType(items);

    // ── Fetch ────────────────────────────────────────────────────────────────

    const fetchAll = useCallback(async ({ bustCache = false } = {}) => {
        if (!practiceId || !leadId) {
            setLoading(false);
            return;
        }

        if (!hasFeatureAccess('tasks-v2', practiceId)) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const apiOpts = bustCache ? { bustCache: true } : {};
        try {
            // Fetch template-checklist items AND the lead's open tasks in parallel.
            // The /tasks/ endpoint is the same source LeadTaskList uses and includes
            // standalone (ad-hoc) tasks that /task-checklists/ never returns.
            const [checklistRes, tasksRes, tplRes] = await Promise.all([
                callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/task-checklists/`,
                    'GET', null, false, apiOpts
                ),
                callApi(
                    `/api/v1/practices/${practiceId}/tasks/?lead=${leadId}&status__in=pending,in_progress&ordering=due_at&limit=50`,
                    'GET', null, false, apiOpts
                ),
                // Templates give each template's applies_to_statuses so we can hide
                // stale prior-stage checklists (e.g. "New Lead Sequence" left open on
                // an ATTENDED lead). See checklistCurrentStatusFilter util header.
                callApi(`/api/v1/practices/${practiceId}/task-templates/?limit=200`, 'GET', null, false, apiOpts),
            ]);

            let tplData = [];
            if (Array.isArray(tplRes?.data?.results)) tplData = tplRes.data.results;
            else if (Array.isArray(tplRes?.data)) tplData = tplRes.data;
            const tplStatusMap = buildTemplateStatusMap(tplData);

            let checklistItems = [];
            if (checklistRes?.status === 200) {
                const rawGroups = checklistRes.data?.results ?? checklistRes.data ?? [];
                const groups = filterGroupsByCurrentStatus(rawGroups, tplStatusMap);
                setRawGroups(groups);
                checklistItems = buildFlatItems(groups);
            } else if (warnedLeadRef.current !== leadId) {
                warnedLeadRef.current = leadId;
                // Non-fatal: modal still works for booking/contact without checklist
                console.warn('[useV2ChecklistAutocomplete] task-checklists fetch failed', checklistRes?.status);
            }

            let standaloneItems = [];
            if (tasksRes?.status === 200) {
                const allTasks = tasksRes.data?.results ?? tasksRes.data ?? [];
                // Only include standalone tasks (template == null). Template-based tasks
                // already appear via the checklist endpoint above — merging them again
                // would show duplicate cards.
                // Filter on template_id (the serialized field name) — t.template is never
                // present on the wire; t.template_id == null catches both null and undefined.
                const standaloneTasks = allTasks.filter((t) => t.template_id == null);
                standaloneItems = buildStandaloneItems(standaloneTasks);
            }
            // Non-fatal: if tasks fetch fails, fall back to checklist items only.

            // Merge and re-sort: OPEN items by due_at ASC (overdue first), done last.
            // Mirrors buildFlatItems sort order so the combined list respects due_at.
            const merged = [...checklistItems, ...standaloneItems];
            merged.sort((a, b) => {
                if (a.completion_state !== b.completion_state) {
                    return a.completion_state === 'OPEN' ? -1 : 1;
                }
                if (!a.due_at && !b.due_at) return 0;
                if (!a.due_at) return 1;
                if (!b.due_at) return -1;
                return new Date(parseApiDate(a.due_at)) - new Date(parseApiDate(b.due_at));
            });
            setRawItems(merged);
        } catch (err) {
            if (warnedLeadRef.current !== leadId) {
                warnedLeadRef.current = leadId;
                console.warn('[useV2ChecklistAutocomplete] fetch error', err?.message);
            }
        } finally {
            setLoading(false);
        }
    }, [practiceId, leadId]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // Explicitly reset queue on leadId change so a queued-but-not-committed
    // batch can never survive across leads if the modal stays mounted (e.g.
    // Dialog keep-mounted perf pattern). Unmount alone is not sufficient.
    useEffect(() => {
        setQueuedIds(new Set());
    }, [leadId]);

    // ── Queue API ─────────────────────────────────────────────────────────────

    /**
     * Queue the oldest open item of the given item_type.
     * No-op if no open item exists or if it's already queued.
     * Oldest-open-wins: due_at ASC (items array is already sorted this way).
     */
    const queueComplete = useCallback((itemType) => {
        const target = items.find(
            (it) => it.item_type === itemType && it.completion_state === 'OPEN' && !queuedIds.has(it.id)
        );
        if (!target) return;
        setQueuedIds((prev) => new Set([...prev, target.id]));
    }, [items, queuedIds]);

    /**
     * Remove a single item from the queue (per-item Undo for queued-but-not-committed items).
     * Does NOT fire any API calls — just removes the ID from queuedIds so the item
     * reverts to OPEN visual state. For already-committed items use reopenChecklistItem thunk.
     */
    const unqueueItem = useCallback((itemId) => {
        setQueuedIds((prev) => {
            const next = new Set(prev);
            next.delete(itemId);
            return next;
        });
    }, []);

    /**
     * Discard all queued completions (called on modal X / Escape).
     * Does NOT fire any API calls.
     */
    const discardQueued = useCallback(() => {
        setQueuedIds(new Set());
    }, []);

    /**
     * Commit all queued completions atomically.
     * Called on wizard Finish — POSTs complete/ for each queued item's taskId.
     * LeadContactedCount is bumped server-side by the checklist completion receiver;
     * no client-side addNotes dispatch is needed.
     * Clears the queue after all POSTs (success or not).
     */
    const commitQueued = useCallback(async () => {
        if (queuedIds.size === 0) return;

        const toCommit = items.filter((it) => queuedIds.has(it.id));

        // 1. POST checklist-item-level complete/ for each queued item.
        // Mirrors taskSlice.js completeChecklistItem (line 310):
        //   POST /practices/<p>/tasks/<taskId>/checklist/<itemId>/complete/
        //   body: { completion_state: 'YES' }
        // (NOT the task-level /tasks/<taskId>/complete/ which marks the ENTIRE task done.)
        const completeResults = await Promise.allSettled(
            toCommit.map((item) =>
                callApi(
                    `/api/v1/practices/${practiceId}/tasks/${item.taskId}/checklist/${item.itemId}/complete/`,
                    'post',
                    { completion_state: 'YES' }
                )
            )
        );

        // Report any complete/ failures — non-fatal
        completeResults.forEach((result, idx) => {
            if (result.status === 'rejected' || (result.value && !result.value.ok)) {
                const detail = result.value?.data?.detail
                    || result.reason?.message
                    || 'Could not complete checklist item';
                dispatch(updateError({ success: false, message: detail }));
                console.warn('[useV2ChecklistAutocomplete] commitQueued partial failure', {
                    itemId: toCommit[idx]?.itemId, detail,
                });
            }
        });

        setQueuedIds(new Set());
        // Refresh items to reflect new COMPLETE states from the API
        fetchAll({ bustCache: true });
    }, [items, queuedIds, practiceId, leadId, fetchAll]);

    return {
        items,
        openItemsByType,
        queuedIds,
        queuedCount: queuedIds.size,
        queueComplete,
        unqueueItem,
        discardQueued,
        commitQueued,
        loading,
        refresh: fetchAll,
        rawGroups, // exposed for debug overlay — not for production UI logic
    };
}
